import { gql, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import Table from '../../components/Table';
import { transactionAnomalyFeedbackForUserPath } from '../../paths';

import type {
  AdminGetUsersWithTransactionReviewCountsQuery,
  AdminGetUsersWithTransactionReviewCountsQueryVariables,
} from './__generated__/UsersWithTransactionReviewsScene.gql';

const ADMIN_GET_USERS_WITH_TRANSACTION_REVIEW_COUNTS = gql`
  query AdminGetUsersWithTransactionReviewCounts {
    adminGetUsersWithTransactionReviewCounts {
      user {
        id
        name
        email
      }
      transactionReviewCount
    }
  }
`;

/**
 * Picker scene for the transaction anomaly feedback tool: lists users who have
 * unreviewed anomalies in the last month with a count, and navigates the
 * operator to the per-user review scene on row click. Entry point referenced
 * from `tools.ts` and `PATHS.TRANSACTION_ANOMALY_FEEDBACK`.
 */
const UsersWithTransactionReviewsScene = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<
    AdminGetUsersWithTransactionReviewCountsQuery,
    AdminGetUsersWithTransactionReviewCountsQueryVariables
  >(ADMIN_GET_USERS_WITH_TRANSACTION_REVIEW_COUNTS, {
    fetchPolicy: 'network-only',
  });

  const users = data?.adminGetUsersWithTransactionReviewCounts ?? [];

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Transaction anomaly feedback</h1>
          <p className="text-fg-tertiary text-xs">
            Users with anomalous transactions awaiting review (last month). Click a row to review their transactions.
          </p>
        </div>

        {loading ? (
          <span className="text-fg-secondary text-xs">Loading…</span>
        ) : error ? (
          <span className="text-fg-error-primary text-xs">Error loading users: {error.message}</span>
        ) : users.length === 0 ? (
          <span className="text-fg-tertiary text-xs italic">No users with transactions requiring review.</span>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Pending review</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((item) => {
                const open = () => navigate(transactionAnomalyFeedbackForUserPath(item.user.id));
                return (
                  <Table.Row
                    key={item.user.id}
                    className="focus-visible:outline-border-brand cursor-pointer focus-visible:outline focus-visible:outline-2"
                    tabIndex={0}
                    role="button"
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void open();
                      }
                    }}
                  >
                    <Table.Cell>{item.user.name}</Table.Cell>
                    <Table.Cell mono muted>
                      {item.user.email}
                    </Table.Cell>
                    <Table.Cell className="text-right">{item.transactionReviewCount}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </main>
    </div>
  );
};

export default UsersWithTransactionReviewsScene;
